import { useEffect, useState } from "react";
import React from 'react';
import { useTranslation } from "react-i18next";
import { Typography, Box, IconButton, Snackbar, Grid, Button, ThemeProvider, CssBaseline, Tooltip, Modal, ModalDialog, ModalClose, DialogTitle, DialogContent, DialogActions, Card, CardContent, Chip, Divider } from "@mui/joy";
import { Delete as DeleteIcon, Edit, KeyboardArrowRight, CheckCircle, Block, WarningRounded } from "@mui/icons-material";
import Breadcrumbs from '@mui/joy/Breadcrumbs';
import Link from '@mui/joy/Link';
import CustomTextField from "../../main/widgets/CustomTextField";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { deleteCustomer, readCustomers, postCustomer, putCustomer, activateCustomer, deactivateCustomer } from "../../main/API/customer";
import DeleteAlert from "../../main/widgets/DeleteAlert";
import { theme, primaryColor } from "../../main/theme/constants";
import { Add } from "@mui/icons-material";
import { usePermissions } from "../../main/hooks/usePermissions";
import PermissionGate from "../../main/components/PermissionGate";
import { PERMISSIONS } from "../../main/context/AuthContext";
import { MuiPhone } from "../../main/widgets/MuiPhone";
import { translateAxiosErrorMessage, translateAggregateBackendMessage } from "../../main/utils/masterApiError";

function isValidEmailFormat(value) {
    const s = String(value).trim();
    if (!s) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

export default function CustomerManagerScreen() {
    const { t } = useTranslation("ui");
    const { hasPermission, canAccessOwnData } = usePermissions();
    
    const [data, setData] = useState(null);
    const [results, setResults] = useState([]);
    const [deleteAlert, setDeleteAlert] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [successDelete, setSuccessDelete] = useState(false);
    const [failedDelete, setFailedDelete] = useState(false);
    const [deleteErrorMsg, setDeleteErrorMsg] = useState("");
    const [searchField, setSearchField] = useState("");
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [id, setId] = useState("");
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 100,
    });

    // Create modal states
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [createForm, setCreateForm] = useState({
        first_name: '',
        last_name: '',
        first_name_ar: '',
        last_name_ar: '',
        phone_number: '+968',
        id_number: '',
        email: '',
    });
    const [createFormErrors, setCreateFormErrors] = useState({});
    const [createLoading, setCreateLoading] = useState(false);
    const [successCreate, setSuccessCreate] = useState(false);
    const [failedCreate, setFailedCreate] = useState(false);
    const [createErrorMessage, setCreateErrorMessage] = useState("");

    // Edit modal states
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editForm, setEditForm] = useState({
        id: '',
        first_name: '',
        last_name: '',
        first_name_ar: '',
        last_name_ar: '',
        phone_number: '+968',
        id_number: '',
        email: '',
    });
    const [editFormErrors, setEditFormErrors] = useState({});
    const [editLoading, setEditLoading] = useState(false);
    const [successEdit, setSuccessEdit] = useState(false);
    const [failedEdit, setFailedEdit] = useState(false);
    const [editErrorMessage, setEditErrorMessage] = useState("");

    // Activate / deactivate states
    const [deactivateAlert, setDeactivateAlert] = useState(false);
    const [activationLoading, setActivationLoading] = useState(false);
    const [activationSuccessMessage, setActivationSuccessMessage] = useState("");
    const [activationSuccessOpen, setActivationSuccessOpen] = useState(false);
    const [activationFailedOpen, setActivationFailedOpen] = useState(false);
    const [activationErrorMessage, setActivationErrorMessage] = useState("");

    // Same design tokens as CustomerForm.js
    const formStyles = {
        cardBg: "#ffffff",
        cardShadow: "0 1px 3px rgba(0,0,0,0.06)",
        cardRadius: "16px",
        cardBorder: "1px solid rgba(0,0,0,0.06)",
        sectionSpacing: 3,
        labelColor: "#475569",
        titleColor: "#1e293b",
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        fetchData(true);
    }

    const handlePageSizeChange = (pageSize) => {
        setPaginationModel({ page: 0, pageSize });
    };

    const handlePageChange = (page) => {
        setPaginationModel((prev) => ({ ...prev, page }));
    };

    const navigate = useNavigate();

    const columns = [
        { field: 'id', headerName: t('customerManager.colId'), flex: 1.5, minWidth: 80 },
        { field: 'first_name', headerName: t('customerManager.colFirstName'), flex: 2, minWidth: 180 },
        { field: 'last_name', headerName: t('customerManager.colLastName'), flex: 2, minWidth: 180 },
        { field: 'first_name_ar', headerName: t('customerManager.colFirstNameAr'), flex: 2, minWidth: 180 },
        { field: 'last_name_ar', headerName: t('customerManager.colLastNameAr'), flex: 2, minWidth: 180 },
        { field: 'phone_number', headerName: t('customerManager.colPhone'), flex: 2, minWidth: 180 },
        { field: 'email', headerName: t('customer.labelEmail'), flex: 2, minWidth: 200 },
        {
            field: 'is_active',
            headerName: t('customerManager.colStatus'),
            flex: 1,
            minWidth: 100,
            renderCell: (params) => {
                const active = params.row.is_active === true;
                return (
                    <Chip size="sm" variant="soft" color={active ? 'success' : 'warning'}>
                        {active ? t('customerManager.statusActive') : t('customerManager.statusInactive')}
                    </Chip>
                );
            },
        },
        {
            field: 'actions',
            headerName: t('customerManager.colActions'),
            sortable: false,
            flex: 2,
            minWidth: 180,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {hasPermission(PERMISSIONS.CUSTOMERS_ACTIVATE) && !params.row.is_active && (
                        <Tooltip title={t('customerManager.tooltipActivate')}>
                            <IconButton
                                size="sm"
                                variant="soft"
                                color="success"
                                disabled={activationLoading}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleActivateCustomer(params.row);
                                }}
                            >
                                <CheckCircle fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {hasPermission(PERMISSIONS.CUSTOMERS_DEACTIVATE) && params.row.is_active && (
                        <Tooltip title={t('customerManager.tooltipDeactivate')}>
                            <IconButton
                                size="sm"
                                variant="soft"
                                color="warning"
                                disabled={activationLoading}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedCustomer(params.row);
                                    setDeactivateAlert(true);
                                }}
                            >
                                <Block fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {(hasPermission(PERMISSIONS.CUSTOMERS_UPDATE) || canAccessOwnData(params.row.owner)) && (
                        <Tooltip title={t('customerManager.tooltipEdit')}>
                            <IconButton
                                size="sm"
                                variant="soft"
                                color="primary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditClick(params.row);
                                }}
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {hasPermission(PERMISSIONS.CUSTOMERS_DELETE) && (
                        <Tooltip title={t('customerManager.tooltipDelete')}>
                            <IconButton
                                size="sm"
                                variant="soft"
                                color="danger"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedCustomer(params.row);
                                    setDeleteAlert(true);
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            ),
        },
    ];

    const deleteCustomerFromTable = async (customer) => {
        if (!hasPermission(PERMISSIONS.CUSTOMERS_DELETE)) {
            setDeleteAlert(false);
            return;
        }
        setDeleteLoading(true);
        try {
            const response = await deleteCustomer(customer);
            if (response.status === 200) {
                const newData = results.filter(item => item.id !== customer.id);
                setResults(newData);
                setSuccessDelete(true);
            } else {
                setFailedDelete(true);
                setDeleteErrorMsg(t('customerManager.deleteFailedDetail', { detail: response.data.detail }));
            }
        } catch (e) {
            setFailedDelete(true);
            setDeleteErrorMsg(t('customerManager.deleteErrorMessage', { message: e.message }));
        }
        setDeleteLoading(false);
        setDeleteAlert(false);
    };

    const updateCustomerInResults = (updatedUser) => {
        setResults((prev) => prev.map((item) => (
            item.id === updatedUser.id ? { ...item, ...updatedUser } : item
        )));
    };

    const handleActivateCustomer = async (customer) => {
        if (!hasPermission(PERMISSIONS.CUSTOMERS_ACTIVATE) || !customer?.id) return;
        setActivationLoading(true);
        try {
            const response = await activateCustomer(customer.id);
            const { message, user } = response.data || {};
            if (user) updateCustomerInResults(user);
            setActivationSuccessMessage(message || t('customerManager.statusActive'));
            setActivationSuccessOpen(true);
        } catch (error) {
            setActivationFailedOpen(true);
            setActivationErrorMessage(translateAxiosErrorMessage(error, t) || t('customerManager.activationFailed'));
        } finally {
            setActivationLoading(false);
        }
    };

    const handleDeactivateCustomer = async () => {
        if (!hasPermission(PERMISSIONS.CUSTOMERS_DEACTIVATE) || !selectedCustomer?.id) {
            setDeactivateAlert(false);
            return;
        }
        setActivationLoading(true);
        try {
            const response = await deactivateCustomer(selectedCustomer.id);
            const { message, user } = response.data || {};
            if (user) updateCustomerInResults(user);
            setActivationSuccessMessage(message || t('customerManager.statusInactive'));
            setActivationSuccessOpen(true);
        } catch (error) {
            setActivationFailedOpen(true);
            setActivationErrorMessage(translateAxiosErrorMessage(error, t) || t('customerManager.activationFailed'));
        } finally {
            setActivationLoading(false);
            setDeactivateAlert(false);
        }
    };

    const fetchData = (isSearch = false) => {
        if (!hasPermission(PERMISSIONS.CUSTOMERS_LIST)) {
            setResults([]);
            setData(null);
            return;
        }
        if (isSearch) {
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }
        setIsLoading(true);
        readCustomers(searchField, id, paginationModel.pageSize, isSearch ? 1 : paginationModel.page + 1)
            .then((response) => response.data)
            .then((actualData) => {
                setData(actualData);
                setResults(actualData.results);
            })
            .catch((err) => console.log(err.message))
            .finally(() => setIsLoading(false));
    };

    // Create customer handlers
    const handleCreateClick = () => {
        setCreateForm({
            first_name: '',
            last_name: '',
            first_name_ar: '',
            last_name_ar: '',
            phone_number: '+968',
            id_number: '',
            email: '',
        });
        setCreateFormErrors({});
        setCreateModalOpen(true);
    };

    const phoneHandleChange = (value) => {
        setCreateForm(prev => ({ ...prev, phone_number: value }));
    };

    const handleCreateSubmit = async () => {
        if (!hasPermission(PERMISSIONS.CUSTOMERS_CREATE)) return;
        // Clear previous errors
        setCreateFormErrors({});

        // Validate required fields
        const errors = {};
        if (!createForm.first_name || createForm.first_name.trim() === '') {
            errors.first_name = t('customerManager.firstNameRequired');
        }
        if (!createForm.first_name_ar || createForm.first_name_ar.trim() === '') {
            errors.first_name_ar = t('customerManager.firstNameArRequired');
        }
        if (!createForm.phone_number || createForm.phone_number.trim() === '') {
            errors.phone_number = t('customerManager.phoneRequired');
        }
        const emailTrimmed = createForm.email.trim();
        if (emailTrimmed && !isValidEmailFormat(emailTrimmed)) {
            errors.email = t('customer.emailInvalid');
        }

        if (Object.keys(errors).length > 0) {
            setCreateFormErrors(errors);
            return;
        }

        setCreateLoading(true);
        try {
            const response = await postCustomer({ ...createForm, email: emailTrimmed });
            
            if (response && response.status === 201) {
                setSuccessCreate(true);
                setCreateModalOpen(false);
                setCreateForm({
                    first_name: '',
                    last_name: '',
                    first_name_ar: '',
                    last_name_ar: '',
                    phone_number: '+968',
                    id_number: '',
                    email: '',
                });
                fetchData(); // Refresh the list
            } else {
                setFailedCreate(true);
                setCreateErrorMessage(response?.statusText || t('customerManager.failedCreate'));
            }
        } catch (error) {
            console.error('Failed to create customer:', error);
            setFailedCreate(true);
            setCreateErrorMessage(translateAxiosErrorMessage(error, t) || t('customerManager.failedCreate'));
            
            // Set field-specific errors if available
            if (error.response && error.response.data) {
                const fieldErrors = {};
                Object.keys(error.response.data).forEach(key => {
                    const errorValue = error.response.data[key];
                    const raw = Array.isArray(errorValue) ? errorValue[0] : errorValue;
                    if (key === 'email' && typeof raw === 'string' && raw.includes('already registered')) {
                        fieldErrors[key] = t('customer.emailAlreadyRegistered');
                    } else {
                        fieldErrors[key] = translateAggregateBackendMessage(String(raw), t);
                    }
                });
                setCreateFormErrors(fieldErrors);
            }
        } finally {
            setCreateLoading(false);
        }
    };

    // Edit customer handlers
    const handleEditClick = (customer) => {
        setEditForm({
            id: customer.id,
            first_name: customer.first_name || '',
            last_name: customer.last_name || '',
            first_name_ar: customer.first_name_ar || '',
            last_name_ar: customer.last_name_ar || '',
            phone_number: customer.phone_number || '+968',
            id_number: customer.id_number || '',
            email: customer.email != null ? String(customer.email).trim() : '',
        });
        setEditFormErrors({});
        setEditModalOpen(true);
    };

    const editPhoneHandleChange = (value) => {
        setEditForm(prev => ({ ...prev, phone_number: value }));
    };

    const handleEditSubmit = async () => {
        if (!hasPermission(PERMISSIONS.CUSTOMERS_UPDATE)) return;
        setEditFormErrors({});
        const errors = {};
        if (!editForm.first_name || editForm.first_name.trim() === '') {
            errors.first_name = t('customerManager.firstNameRequired');
        }
        if (!editForm.first_name_ar || editForm.first_name_ar.trim() === '') {
            errors.first_name_ar = t('customerManager.firstNameArRequired');
        }
        if (!editForm.phone_number || editForm.phone_number.trim() === '') {
            errors.phone_number = t('customerManager.phoneRequired');
        }
        const emailTrimmed = editForm.email.trim();
        if (emailTrimmed && !isValidEmailFormat(emailTrimmed)) {
            errors.email = t('customer.emailInvalid');
        }
        if (Object.keys(errors).length > 0) {
            setEditFormErrors(errors);
            return;
        }
        setEditLoading(true);
        try {
            const response = await putCustomer({ ...editForm, email: emailTrimmed });
            if (response && response.status === 200) {
                setSuccessEdit(true);
                setEditModalOpen(false);
                fetchData();
            } else {
                setFailedEdit(true);
                setEditErrorMessage(response?.statusText || t('customerManager.failedUpdate'));
            }
        } catch (error) {
            console.error('Failed to update customer:', error);
            setFailedEdit(true);
            setEditErrorMessage(translateAxiosErrorMessage(error, t) || t('customerManager.failedUpdate'));
            if (error.response?.data) {
                const fieldErrors = {};
                Object.keys(error.response.data).forEach(key => {
                    const v = error.response.data[key];
                    const raw = Array.isArray(v) ? v[0] : v;
                    if (key === 'email' && typeof raw === 'string' && raw.includes('already registered')) {
                        fieldErrors[key] = t('customer.emailAlreadyRegistered');
                    } else {
                        fieldErrors[key] = translateAggregateBackendMessage(String(raw), t);
                    }
                });
                setEditFormErrors(fieldErrors);
            }
        } finally {
            setEditLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [paginationModel.page, paginationModel.pageSize]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <form onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex' }} m={3}>
                    <Box width={"100%"}>
                        <Box>
                            <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
                                {[t('nav.customers'), t('customerManager.breadcrumbManager')].map((item) => (
                                    <Link key={item} color="neutral" href="/customers">
                                        {item}
                                    </Link>
                                ))}
                            </Breadcrumbs>
                            <Typography textAlign={"start"} level="h1">{t('customerManager.pageTitle')}</Typography>
                        </Box>

                        {/* Filter customers field */}
                        <PermissionGate permissions={[PERMISSIONS.CUSTOMERS_LIST]}>
                            <Grid container spacing={1} sx={{ flexGrow: 1, mb: 1 }}>
                                <Grid item xs={12}>
                                    <Typography level="h3" textAlign={"start"}>{t('customerManager.filterHeading')}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3} lg={3}>
                                    <CustomTextField disabled={searchField != ""} type="text" onChange={(e) => setId(e.target.value)} value={id} label={t('customerManager.colId')} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3} lg={3}>
                                    <CustomTextField disabled={id != ""} label={t('actions.all')} type="text" onChange={(e) => setSearchField(e.target.value)} value={searchField} />
                                </Grid>

                            </Grid>
                            <Button type="submit" loading={isLoading}>
                                {t('actions.search')}
                            </Button>
                        </PermissionGate>

                        {/* Actions icons only */}
                        <Grid mt={2} container spacing={2} justifyContent="flex-end">
                            <Grid
                                container
                                spacing={1}
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    justifyContent: 'flex-start'
                                }}
                            >

                                <PermissionGate permission={PERMISSIONS.CUSTOMERS_CREATE}>
                                    <Grid item>
                                        <Button
                                            startDecorator={<Add />}
                                            onClick={handleCreateClick}
                                            color="primary"
                                            variant="solid"
                                            sx={{
                                                mr: { xs: 0, md: 1 },
                                                width: { xs: '100%', md: 'auto' },
                                            }}
                                        >
                                            {t('customerManager.addCustomer')}
                                        </Button>
                                    </Grid>
                                </PermissionGate>
                            </Grid>
                        </Grid>

                        <PermissionGate permissions={[PERMISSIONS.CUSTOMERS_LIST]}>
                            <Box>
                                <DataGrid
                                    onPaginationModelChange={(params) => {
                                    if (params.pageSize !== paginationModel.pageSize) {
                                        handlePageSizeChange(params.pageSize);
                                    } else {
                                        handlePageChange(params.page);
                                    }
                                }}
                                pagination
                                paginationModel={paginationModel}
                                paginationMode="server"
                                rowCount={data ? data.count : 0}
                                rows={results || []}
                                columns={columns}
                                loading={isLoading}
                                initialState={{ pagination: { paginationModel } }}
                                pageSizeOptions={[25, 50, 100]}
                                hideFooterSelectedRowCount
                                getRowHeight={() => 'auto'}
                                sx={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                    '& .MuiDataGrid-root': {
                                        border: 'none',
                                    },
                                    '& .MuiDataGrid-columnHeaders': {
                                        backgroundColor: '#f5f5f5',
                                        borderBottom: '2px solid #e0e0e0',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: '#333',
                                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                        '& .MuiDataGrid-columnHeaderTitle': {
                                            fontWeight: 600,
                                            fontSize: '14px',
                                            color: '#333',
                                        },
                                    },
                                    '& .MuiDataGrid-cell': {
                                        whiteSpace: 'normal',
                                        wordWrap: 'break-word',
                                        lineHeight: '1.5',
                                        padding: '12px 16px',
                                        fontSize: '14px',
                                        color: '#555',
                                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                        borderBottom: '1px solid #f0f0f0',
                                    },
                                    '& .MuiDataGrid-row': {
                                        '&:hover': {
                                            backgroundColor: '#f8f9fa',
                                            cursor: 'pointer',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: '#e3f2fd',
                                            '&:hover': {
                                                backgroundColor: '#bbdefb',
                                            },
                                        },
                                    },
                                    '& .MuiDataGrid-footerContainer': {
                                        borderTop: '2px solid #e0e0e0',
                                        backgroundColor: '#fafafa',
                                        fontSize: '14px',
                                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                    },
                                    '& .MuiDataGrid-toolbarContainer': {
                                        padding: '12px',
                                        backgroundColor: '#fafafa',
                                        borderBottom: '1px solid #e0e0e0',
                                    },
                                    '@media (max-width: 600px)': {
                                        '& .MuiDataGrid-toolbarContainer': {
                                            flexDirection: 'column',
                                            gap: 1,
                                        },
                                        '& .MuiDataGrid-footer': {
                                            flexDirection: 'column',
                                            gap: 1,
                                        },
                                        '& .MuiDataGrid-cell': {
                                            minHeight: '60px',
                                            alignItems: 'center',
                                            padding: '8px',
                                            fontSize: '13px',
                                        },
                                        '& .MuiDataGrid-columnHeaders': {
                                            fontSize: '13px',
                                        },
                                    }
                                }}
                            />
                            </Box>
                        </PermissionGate>

                        <DeleteAlert
                            label={t('widgets.customer')}
                            open={deleteAlert}
                            onDelete={() => deleteCustomerFromTable(selectedCustomer)}
                            onClose={() => setDeleteAlert(false)}
                            loading={deleteLoading}
                        />

                        <Modal open={deactivateAlert} onClose={() => !activationLoading && setDeactivateAlert(false)}>
                            <ModalDialog variant="outlined" role="alertdialog">
                                <DialogTitle>
                                    <WarningRounded />
                                    {t('customerManager.deactivateTitle')}
                                </DialogTitle>
                                <Divider />
                                <DialogContent>
                                    {t('customerManager.deactivateMessage')}
                                </DialogContent>
                                <DialogActions>
                                    <Button
                                        loading={activationLoading}
                                        variant="solid"
                                        color="warning"
                                        onClick={activationLoading ? null : handleDeactivateCustomer}
                                    >
                                        {t('customerManager.deactivateConfirm')}
                                    </Button>
                                    <Button variant="plain" color="neutral" onClick={() => setDeactivateAlert(false)} disabled={activationLoading}>
                                        {t('actions.cancel')}
                                    </Button>
                                </DialogActions>
                            </ModalDialog>
                        </Modal>

                        <Snackbar
                            autoHideDuration={3000}
                            open={successDelete}
                            variant={"soft"}
                            color={"success"}
                            onClose={(event, reason) => {
                                if (reason === 'clickaway') return;
                                setSuccessDelete(false);
                            }}
                        >
                            {t('customerManager.deletedSuccess')}
                        </Snackbar>

                        <Snackbar
                            autoHideDuration={3000}
                            open={failedDelete}
                            variant={"soft"}
                            color={"danger"}
                            onClose={(event, reason) => {
                                if (reason === 'clickaway') return;
                                setFailedDelete(false);
                            }}
                        >
                            {deleteErrorMsg}
                        </Snackbar>

                        <Snackbar
                            autoHideDuration={3000}
                            open={activationSuccessOpen}
                            variant="soft"
                            color="success"
                            onClose={(event, reason) => {
                                if (reason === 'clickaway') return;
                                setActivationSuccessOpen(false);
                            }}
                        >
                            {activationSuccessMessage}
                        </Snackbar>

                        <Snackbar
                            autoHideDuration={3000}
                            open={activationFailedOpen}
                            variant="soft"
                            color="danger"
                            onClose={(event, reason) => {
                                if (reason === 'clickaway') return;
                                setActivationFailedOpen(false);
                            }}
                        >
                            {activationErrorMessage || t('customerManager.activationFailed')}
                        </Snackbar>

                        {/* Create Customer Modal - same design as CustomerForm.js */}
                        <Modal open={createModalOpen} onClose={() => setCreateModalOpen(false)}>
                            <ModalDialog
                                layout="fullscreen"
                                sx={{
                                    width: { xs: '100%', sm: '90%', md: '700px', lg: '800px' },
                                    maxWidth: '800px',
                                    maxHeight: { xs: '100vh', sm: '90vh' },
                                    margin: { xs: 0, sm: 'auto' },
                                    borderRadius: { xs: 0, sm: '12px' },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden',
                                }}
                            >
                                <ModalClose />
                                <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: formStyles.titleColor, fontWeight: 700, pb: 2, flexShrink: 0 }}>
                                    {t('customerManager.modalAddTitle')}
                                </DialogTitle>
                                <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                                    <Box sx={{ overflowY: 'auto', overflowX: 'hidden', flex: 1, minHeight: 0, maxHeight: { xs: 'calc(100vh - 140px)', sm: 'calc(90vh - 140px)' }, px: { xs: 2, sm: 3 }, py: 2, WebkitOverflowScrolling: 'touch' }}>
                                        <Card variant="plain" sx={{ bgcolor: formStyles.cardBg, boxShadow: formStyles.cardShadow, borderRadius: formStyles.cardRadius, border: formStyles.cardBorder, overflow: 'hidden' }}>
                                            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                                                <Typography level="body2" sx={{ color: formStyles.labelColor, fontWeight: 600, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.75rem' }}>{t('customerManager.sectionName')}</Typography>
                                                <Grid container spacing={2} sx={{ mb: formStyles.sectionSpacing }}>
                                                    <Grid xs={12} sm={6}>
                                                        <Box>
                                                            <CustomTextField slotProps={{ input: { minLength: 1, maxLength: 30 } }} name="first_name" required type="text" label={t('customerManager.labelFirstName')} onChange={(e) => { setCreateForm(prev => ({ ...prev, first_name: e.target.value })); if (createFormErrors.first_name) setCreateFormErrors(prev => ({ ...prev, first_name: '' })); }} value={createForm.first_name} xs={12} />
                                                            {createFormErrors.first_name && <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>{createFormErrors.first_name}</Typography>}
                                                        </Box>
                                                    </Grid>
                                                    <Grid xs={12} sm={6}>
                                                        <CustomTextField slotProps={{ input: { minLength: 1, maxLength: 30 } }} name="last_name" type="text" label={t('customerManager.labelLastName')} onChange={(e) => setCreateForm(prev => ({ ...prev, last_name: e.target.value }))} value={createForm.last_name} xs={12} />
                                                    </Grid>
                                                </Grid>
                                                <Typography level="body2" sx={{ color: formStyles.labelColor, fontWeight: 600, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.75rem' }}>{t('customerManager.sectionNameArabic')}</Typography>
                                                <Grid container spacing={2} sx={{ mb: formStyles.sectionSpacing }}>
                                                    <Grid xs={12} sm={6}>
                                                        <Box>
                                                            <CustomTextField slotProps={{ input: { minLength: 1, maxLength: 30 } }} name="first_name_ar" required type="text" label={t('customerManager.labelFirstNameAr')} onChange={(e) => { setCreateForm(prev => ({ ...prev, first_name_ar: e.target.value })); if (createFormErrors.first_name_ar) setCreateFormErrors(prev => ({ ...prev, first_name_ar: '' })); }} value={createForm.first_name_ar} xs={12} />
                                                            {createFormErrors.first_name_ar && <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>{createFormErrors.first_name_ar}</Typography>}
                                                        </Box>
                                                    </Grid>
                                                    <Grid xs={12} sm={6}>
                                                        <CustomTextField slotProps={{ input: { minLength: 1, maxLength: 30 } }} name="last_name_ar" type="text" label={t('customerManager.labelLastNameAr')} onChange={(e) => setCreateForm(prev => ({ ...prev, last_name_ar: e.target.value }))} value={createForm.last_name_ar} xs={12} />
                                                    </Grid>
                                                </Grid>
                                                <Typography level="body2" sx={{ color: formStyles.labelColor, fontWeight: 600, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.75rem' }}>{t('customerManager.sectionContactId')}</Typography>
                                                <Grid container spacing={2} sx={{ mb: formStyles.sectionSpacing }}>
                                                    <Grid xs={12} sm={6}>
                                                        <CustomTextField slotProps={{ input: { minLength: 1, maxLength: 50 } }} name="id_number" type="text" label={t('customerManager.labelIdNumber')} onChange={(e) => setCreateForm(prev => ({ ...prev, id_number: e.target.value }))} value={createForm.id_number} xs={12} />
                                                    </Grid>
                                                    <Grid xs={12} sm={6}>
                                                        <Box>
                                                            <CustomTextField slotProps={{ input: { maxLength: 254 } }} name="email" type="email" label={t('customer.labelEmail')} onChange={(e) => { setCreateForm(prev => ({ ...prev, email: e.target.value })); if (createFormErrors.email) setCreateFormErrors(prev => ({ ...prev, email: '' })); }} value={createForm.email} xs={12} />
                                                            {createFormErrors.email && <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>{createFormErrors.email}</Typography>}
                                                        </Box>
                                                    </Grid>
                                                    <Grid xs={12} sm={6}>
                                                        <Box>
                                                            <MuiPhone required value={createForm.phone_number} onChange={phoneHandleChange} />
                                                            {createFormErrors.phone_number && <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>{createFormErrors.phone_number}</Typography>}
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', pt: 2, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                                                    <Button variant="plain" color="neutral" onClick={() => setCreateModalOpen(false)}>{t('actions.cancel')}</Button>
                                                    <Button onClick={handleCreateSubmit} loading={createLoading} sx={{ bgcolor: primaryColor, '&:hover': { bgcolor: '#4b059a' }, borderRadius: '10px', px: 3, fontWeight: 600 }}>{t('actions.submit')}</Button>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Box>
                                </DialogContent>
                            </ModalDialog>
                        </Modal>

                        {/* Edit Customer Modal - same design as CustomerForm.js */}
                        <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
                            <ModalDialog
                                layout="fullscreen"
                                sx={{
                                    width: { xs: '100%', sm: '90%', md: '700px', lg: '800px' },
                                    maxWidth: '800px',
                                    maxHeight: { xs: '100vh', sm: '90vh' },
                                    margin: { xs: 0, sm: 'auto' },
                                    borderRadius: { xs: 0, sm: '12px' },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden',
                                }}
                            >
                                <ModalClose />
                                <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: formStyles.titleColor, fontWeight: 700, pb: 2, flexShrink: 0 }}>
                                    {t('customerManager.modalUpdateTitle')}
                                </DialogTitle>
                                <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                                    <Box sx={{ overflowY: 'auto', overflowX: 'hidden', flex: 1, minHeight: 0, maxHeight: { xs: 'calc(100vh - 140px)', sm: 'calc(90vh - 140px)' }, px: { xs: 2, sm: 3 }, py: 2, WebkitOverflowScrolling: 'touch' }}>
                                        <Card variant="plain" sx={{ bgcolor: formStyles.cardBg, boxShadow: formStyles.cardShadow, borderRadius: formStyles.cardRadius, border: formStyles.cardBorder, overflow: 'hidden' }}>
                                            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                                                <Box sx={{ mb: formStyles.sectionSpacing }}>
                                                    <CustomTextField name="id" disabled type="text" value={editForm.id} label={t('customerManager.colId')} xs={12} />
                                                </Box>
                                                <Typography level="body2" sx={{ color: formStyles.labelColor, fontWeight: 600, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.75rem' }}>{t('customerManager.sectionName')}</Typography>
                                                <Grid container spacing={2} sx={{ mb: formStyles.sectionSpacing }}>
                                                    <Grid xs={12} sm={6}>
                                                        <Box>
                                                            <CustomTextField slotProps={{ input: { minLength: 1, maxLength: 30 } }} name="first_name" required type="text" label={t('customerManager.labelFirstName')} onChange={(e) => { setEditForm(prev => ({ ...prev, first_name: e.target.value })); if (editFormErrors.first_name) setEditFormErrors(prev => ({ ...prev, first_name: '' })); }} value={editForm.first_name} xs={12} />
                                                            {editFormErrors.first_name && <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>{editFormErrors.first_name}</Typography>}
                                                        </Box>
                                                    </Grid>
                                                    <Grid xs={12} sm={6}>
                                                        <CustomTextField slotProps={{ input: { minLength: 1, maxLength: 30 } }} name="last_name" type="text" label={t('customerManager.labelLastName')} onChange={(e) => setEditForm(prev => ({ ...prev, last_name: e.target.value }))} value={editForm.last_name} xs={12} />
                                                    </Grid>
                                                </Grid>
                                                <Typography level="body2" sx={{ color: formStyles.labelColor, fontWeight: 600, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.75rem' }}>{t('customerManager.sectionNameArabic')}</Typography>
                                                <Grid container spacing={2} sx={{ mb: formStyles.sectionSpacing }}>
                                                    <Grid xs={12} sm={6}>
                                                        <Box>
                                                            <CustomTextField slotProps={{ input: { minLength: 1, maxLength: 30 } }} name="first_name_ar" required type="text" label={t('customerManager.labelFirstNameAr')} onChange={(e) => { setEditForm(prev => ({ ...prev, first_name_ar: e.target.value })); if (editFormErrors.first_name_ar) setEditFormErrors(prev => ({ ...prev, first_name_ar: '' })); }} value={editForm.first_name_ar} xs={12} />
                                                            {editFormErrors.first_name_ar && <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>{editFormErrors.first_name_ar}</Typography>}
                                                        </Box>
                                                    </Grid>
                                                    <Grid xs={12} sm={6}>
                                                        <CustomTextField slotProps={{ input: { minLength: 1, maxLength: 30 } }} name="last_name_ar" type="text" label={t('customerManager.labelLastNameAr')} onChange={(e) => setEditForm(prev => ({ ...prev, last_name_ar: e.target.value }))} value={editForm.last_name_ar} xs={12} />
                                                    </Grid>
                                                </Grid>
                                                <Typography level="body2" sx={{ color: formStyles.labelColor, fontWeight: 600, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.75rem' }}>{t('customerManager.sectionContactId')}</Typography>
                                                <Grid container spacing={2} sx={{ mb: formStyles.sectionSpacing }}>
                                                    <Grid xs={12} sm={6}>
                                                        <CustomTextField slotProps={{ input: { minLength: 1, maxLength: 50 } }} name="id_number" type="text" label={t('customerManager.labelIdNumber')} onChange={(e) => setEditForm(prev => ({ ...prev, id_number: e.target.value }))} value={editForm.id_number} xs={12} />
                                                    </Grid>
                                                    <Grid xs={12} sm={6}>
                                                        <Box>
                                                            <CustomTextField slotProps={{ input: { maxLength: 254 } }} name="email" type="email" label={t('customer.labelEmail')} onChange={(e) => { setEditForm(prev => ({ ...prev, email: e.target.value })); if (editFormErrors.email) setEditFormErrors(prev => ({ ...prev, email: '' })); }} value={editForm.email} xs={12} />
                                                            {editFormErrors.email && <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>{editFormErrors.email}</Typography>}
                                                        </Box>
                                                    </Grid>
                                                    <Grid xs={12} sm={6}>
                                                        <Box>
                                                            <MuiPhone required value={editForm.phone_number} onChange={editPhoneHandleChange} />
                                                            {editFormErrors.phone_number && <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>{editFormErrors.phone_number}</Typography>}
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', pt: 2, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                                                    <Button variant="plain" color="neutral" onClick={() => setEditModalOpen(false)}>{t('actions.cancel')}</Button>
                                                    <Button onClick={handleEditSubmit} loading={editLoading} sx={{ bgcolor: primaryColor, '&:hover': { bgcolor: '#4b059a' }, borderRadius: '10px', px: 3, fontWeight: 600 }}>{t('invoiceForm.update')}</Button>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Box>
                                </DialogContent>
                            </ModalDialog>
                        </Modal>

                        {/* Create Success Notification */}
                        <Snackbar
                            autoHideDuration={3000}
                            open={successCreate}
                            variant="soft"
                            color="success"
                            onClose={(event, reason) => {
                                if (reason === 'clickaway') return;
                                setSuccessCreate(false);
                            }}
                        >
                            {t('customerManager.createdSuccess')}
                        </Snackbar>

                        {/* Create Failed Notification */}
                        <Snackbar
                            autoHideDuration={3000}
                            open={failedCreate}
                            variant="soft"
                            color="danger"
                            onClose={(event, reason) => {
                                if (reason === 'clickaway') return;
                                setFailedCreate(false);
                            }}
                        >
                            {createErrorMessage || t('customerManager.failedCreate')}
                        </Snackbar>

                        {/* Edit Success Notification */}
                        <Snackbar
                            autoHideDuration={3000}
                            open={successEdit}
                            variant="soft"
                            color="success"
                            onClose={(event, reason) => {
                                if (reason === 'clickaway') return;
                                setSuccessEdit(false);
                            }}
                        >
                            {t('customerManager.updatedSuccess')}
                        </Snackbar>

                        {/* Edit Failed Notification */}
                        <Snackbar
                            autoHideDuration={3000}
                            open={failedEdit}
                            variant="soft"
                            color="danger"
                            onClose={(event, reason) => {
                                if (reason === 'clickaway') return;
                                setFailedEdit(false);
                            }}
                        >
                            {editErrorMessage || t('customerManager.failedUpdate')}
                        </Snackbar>
                    </Box>
                </Box>
            </form>
        </ThemeProvider>
    );
}
